import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Accordion, AccordionSummary, AccordionDetails, IconButton, Typography } from '@material-ui/core';
import { Badge, Button, Group, Loader, Skeleton, Stack, Tooltip } from '@mantine/core';
import Autoload from '../../../../neoverv/Autoload';
import APIClient from '../../../../neoverv/config/APIClient';
import { config } from '../../../../neoverv/config/Setting';
import SmokeDetectorDataHandler from '../../../../neoverv/helpers/DataHandler/SmokeDetector';
import SmokeDetectorRentalUnitGroup from '../../../../neoverv/components/Module/General/SmokeDetector/SmokeDetectorRentalUnitGroup';
import { showSmokeDetectorExportError } from '../../../../neoverv/helpers/SmokeDetectorExport';
import { SMOKE_DETECTOR_BADGE_COLORS } from '../../../../helpers/smokeDetectorBadgeColors';
import { EmptyStateContainer } from '../../../../neoverv';
import MeterIcon from '../../../../assets/images/module/general/blueEmptyMeter.svg';
import PropertyIcon from '../../../../assets/images/module/general/PropertyIcon.svg';
import MultipleTenants from '../../../../assets/images/module/general/MultipleTenants.svg';
import uparrow from '../../../../assets/images/module/general/SelectArrowUp.svg';
import downArrow from '../../../../assets/images/module/general/SelectArrowDown.svg';
import PdfIcon from '../../../../assets/images/module/general/PdfNewIcon.svg';
import HelpLogo from '../../../../assets/images/module/general/book.svg';
import breadcrumbsContent from '../../../../assets/breadcrumbs.json';

dayjs.extend(utc);
dayjs.extend(timezone);

const PAGE_SIZE = 5;

// Fixed pixel widths (not just gap/flex) for every "column" in the accordion header, so each one
// starts at the same x-position on every row regardless of that row's own content length (e.g.
// "14 Rauchmelder" vs "4 Rauchmelder", "OK" vs "2 Fehler"). A flex Group alone can't do this —
// each badge naturally sizes to its own text, which is exactly what drifts the columns out of
// alignment row to row.
const HEADER_COLUMN_WIDTHS = { dateTime: 145, detectorsFound: 115, statusPrimary: 70, statusWarning: 105 };

const StatusBadge = ({ hasError, hasWarning, detectorsFound, errorCount, warningCount }) => {
  const showOk = detectorsFound > 0 && !hasError && !hasWarning;
  return (
    <Group gap={0} wrap="nowrap">
      <div style={{ width: HEADER_COLUMN_WIDTHS.statusPrimary }}>
        {showOk && (
          <Badge
            className='smoke-detector-screen-badge'
            style={{ color: SMOKE_DETECTOR_BADGE_COLORS.ok.color, backgroundColor: SMOKE_DETECTOR_BADGE_COLORS.ok.background }}
          >
            OK
          </Badge>
        )}
        {hasError && (
          <Badge
            className='smoke-detector-screen-badge'
            style={{ color: SMOKE_DETECTOR_BADGE_COLORS.error.color, backgroundColor: SMOKE_DETECTOR_BADGE_COLORS.error.background }}
          >
            {errorCount} Fehler
          </Badge>
        )}
      </div>
      <div style={{ width: HEADER_COLUMN_WIDTHS.statusWarning }}>
        {hasWarning && (
          <Badge
            className='smoke-detector-screen-badge'
            style={{ color: SMOKE_DETECTOR_BADGE_COLORS.warning.color, backgroundColor: SMOKE_DETECTOR_BADGE_COLORS.warning.background }}
          >
            {warningCount} Warnung{warningCount > 1 ? 'en' : ''}
          </Badge>
        )}
      </div>
    </Group>
  );
};

// Nice-to-have coverage check, folded directly into the "N Rauchmelder" badge (no separate badge
// anymore): compares this reading's found smoke-detector meterNos against every smoke-detector
// meter currently configured on the property (fetched once, independent of any single reading —
// see installedDetectorMeterNos below). Stays neutral gray when there's nothing to compare
// against (property has no smoke detectors configured, or the fetch hasn't resolved yet).
const DetectorsFoundBadge = ({ reading, installedDetectorMeterNos }) => {
  const hasCoverageData = installedDetectorMeterNos.length > 0;
  let colors = SMOKE_DETECTOR_BADGE_COLORS.neutral;
  let tooltipLabel = null;
  let isIncomplete = false;

  if (hasCoverageData) {
    const foundMeterNos = new Set(reading.detectors.map((detector) => detector.meterNo));
    const missingMeterNos = installedDetectorMeterNos.filter((meterNo) => !foundMeterNos.has(meterNo));
    isIncomplete = missingMeterNos.length > 0;
    colors = isIncomplete ? SMOKE_DETECTOR_BADGE_COLORS.error : SMOKE_DETECTOR_BADGE_COLORS.ok;
    tooltipLabel = isIncomplete
      ? `Folgende Rauchmelder wurden nicht überprüft (Daten fehlen): ${missingMeterNos.join(', ')}`
      : 'Alle Rauchwarnmelder wurden überprüft';
  }

  const badge = (
    <Badge className='smoke-detector-screen-badge' style={{ color: colors.color, backgroundColor: colors.background }}>
      {reading.detectorsFound} Rauchmelder
    </Badge>
  );

  if (!tooltipLabel) return badge;

  return (
    <Tooltip label={tooltipLabel} withArrow multiline w={isIncomplete ? 320 : undefined}>
      {badge}
    </Tooltip>
  );
};

// Bulk-exports every rental-unit/property section of one reading run at once, reusing each
// section's own exportBlob(format) (exposed via ref by SmokeDetectorRentalUnitGroup) rather than
// duplicating the PDF generation logic. If more than one section produced a file, they're
// zipped together (matching the JSZip + file-saver pattern BulkDownloadForReceiptsModal.jsx uses);
// with exactly one section, that single file is downloaded directly, no zip needed.
const BulkDownloadButton = ({ reading, groupRefs }) => {
  const [downloading, setDownloading] = useState(false);

  const handleBulkDownload = async () => {
    const entries = Object.values(groupRefs.current).filter(Boolean);
    if (entries.length === 0) return;
    setDownloading(true);

    try {
      const results = (await Promise.all(entries.map((entry) => entry.exportBlob()))).filter(Boolean);
      if (results.length === 0) {
        showSmokeDetectorExportError();
        return;
      }

      const dateStr = reading.readingDateTime ? dayjs(reading.readingDateTime).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

      if (results.length === 1) {
        saveAs(results[0].blob, results[0].fileName);
      } else {
        const zip = new JSZip();
        results.forEach(({ blob, fileName }) => zip.file(fileName, blob));
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `Rauchwarnmelder-Protokolle_${dateStr}.zip`);
      }
    } catch (e) {
      showSmokeDetectorExportError();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Group gap={5} wrap="nowrap" onClick={(event) => event.stopPropagation()}>
      <span style={{ color: '#454576', fontSize: '14px' }}>Alle herunterladen</span>
      <Tooltip label="Alle als PDF herunterladen" withArrow>
        <IconButton
          aria-label="Alle als PDF herunterladen"
          disabled={downloading}
          onClick={handleBulkDownload}
          style={{ background: 'white', border: '1px solid #DADAE5', boxShadow: '0 1px 2px 0 rgba(14, 14, 29, .06)', padding: '5px' }}
        >
          {downloading ? <Loader size="xs" /> : <img src={PdfIcon} width="15px" alt="PDF" />}
        </IconButton>
      </Tooltip>
    </Group>
  );
};

// Loading placeholder shaped like one collapsed reading row (arrow + date column + badge column)
// rather than a single blob overlaying the whole list — gives a sense of "N rows incoming" instead
// of just "something is loading".
const ReadingRowSkeleton = () => (
  <div>
    <div style={{ padding: '12px 16px' }}>
      <Group gap={0} wrap="nowrap">
        <Skeleton height={16} width={16} />
        <Skeleton height={16} width={HEADER_COLUMN_WIDTHS.dateTime} ml={12} />
        <Skeleton height={22} width={90} radius="xl" ml={25} />
        <Skeleton height={22} width={70} radius="xl" ml={16} />
      </Group>
    </div>
    <hr />
  </div>
);

const ReadingAccordion = ({ reading, propertySelected, profileData, accountData, installedDetectorMeterNos }) => {
  const [expanded, setExpanded] = useState(false);
  // Keyed by rentalUnitId ('property' for the Rauchwarnmelder Gebäude section) — populated via each
  // SmokeDetectorRentalUnitGroup's ref callback so bulk export can reach every section without
  // needing them expanded/visible first (MUI's Accordion keeps AccordionDetails mounted at all
  // times by default, it just hides it visually, so the refs are available regardless).
  const groupRefs = useRef({});
  const registerGroupRef = (key) => (instance) => {
    if (instance) {
      groupRefs.current[key] = instance;
    } else {
      delete groupRefs.current[key];
    }
  };

  return (
    <div>
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary>
          <Typography style={{ width: '100%' }}>
            <Group justify="space-between" wrap="nowrap" style={{ width: '100%' }}>
              <Group gap={0} wrap="nowrap">
                <img src={expanded ? uparrow : downArrow} alt="" style={{ minWidth: '8px' }} />
                <span style={{ display: 'inline-block', width: HEADER_COLUMN_WIDTHS.dateTime, fontSize: '16px', fontWeight: '600', color: '#000000', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                {reading.readingDateTime
                  ? dayjs
                      .utc(reading.readingDateTime)
                      .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
                      .format('DD.MM.YYYY (HH:mm)')
                  : '-'}
                </span>
                <div style={{ width: HEADER_COLUMN_WIDTHS.detectorsFound, marginLeft: '25px' }}>
                  <DetectorsFoundBadge reading={reading} installedDetectorMeterNos={installedDetectorMeterNos} />
                </div>
                <StatusBadge
                  hasError={reading.hasError}
                  hasWarning={reading.hasWarning}
                  detectorsFound={reading.detectorsFound}
                  errorCount={reading.errorCount}
                  warningCount={reading.warningCount}
                />
              </Group>
              {reading.detectorsFound > 0 && <BulkDownloadButton reading={reading} groupRefs={groupRefs} />}
            </Group>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography style={{ width: '100%' }}>
            {reading.detectorsFound === 0 ? (
              <EmptyStateContainer
                heading="Keine Rauchwarnmelder gefunden"
                subheading="Diese Ablesung hat keine Rauchwarnmelder-Werte enthalten."
                icon={MeterIcon}
                showBtn={false}
                styleMainContainer={{ marginTop: '10px', marginBottom: '10px' }}
              />
            ) : (
              <>
                {reading.propertyDetectors.length > 0 && (
                  <SmokeDetectorRentalUnitGroup
                    ref={registerGroupRef('property')}
                    rentalUnitId={null}
                    rentalUnitName="Rauchwarnmelder Gebäude"
                    icon={PropertyIcon}
                    detectors={reading.propertyDetectors}
                    readingDateTime={reading.readingDateTime}
                    propertySelected={propertySelected}
                    profileData={profileData}
                    accountData={accountData}
                  />
                )}
                {reading.rentalUnitGroups.length > 0 && (
                  <Group wrap='nowrap' gap={10} style={{ marginTop: '24px' }}>
                    <img src={MultipleTenants} alt="" />
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#0E0E1D', fontFamily: 'Inter, sans-serif' }}>
                      Rauchwarnmelder Mieteinheiten
                    </span>
                  </Group>
                )}
                {reading.rentalUnitGroups.map((group) => (
                  <SmokeDetectorRentalUnitGroup
                    key={group.rentalUnitId || 'unassigned'}
                    ref={registerGroupRef(group.rentalUnitId || 'unassigned')}
                    rentalUnitId={group.rentalUnitId}
                    rentalUnitName={group.rentalUnitName}
                    detectors={group.detectors}
                    readingDateTime={reading.readingDateTime}
                    propertySelected={propertySelected}
                    profileData={profileData}
                    accountData={accountData}
                  />
                ))}
              </>
            )}
          </Typography>
        </AccordionDetails>
      </Accordion>
      <hr />
    </div>
  );
};

const SmokeDetectorReadingsLog = ({ propertySelected, profileData, accountData, isMobile }) => {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [installedDetectorMeterNos, setInstalledDetectorMeterNos] = useState([]);

  // Two-step fetch against the existing, already-deployed generic meter endpoints (there is no
  // dedicated smoke-detector endpoint): MeterReadingLogs lists automated reading runs across ALL
  // meter types, then readings/{readingId} returns the full per-meter detail for one run, which
  // we filter down to smoke-detector meterReadings only (see SmokeDetectorDataHandler). Every
  // MeterReadingLogs row is shown chronologically regardless of meter type mix — a run with no
  // smoke detectors just renders as "0 Rauchmelder" rather than being hidden. A row is only dropped
  // here if its detail fetch itself failed (details[index] is null).
  const fetchBatch = useCallback(
    (currentOffset, append) => {
      if (!propertySelected?.id) return;
      (append ? setLoadingMore : setLoading)(true);

      APIClient({
        method: 'GET',
        url: `${config.meter}MeterReadingLogs?propertyId=${propertySelected.id}&offset=${currentOffset}&fetch=${PAGE_SIZE}`,
        headers: { Authorization: Autoload.Utils.getAuthToken() },
      })
        .then(async (response) => {
          if (!(response.data.status && response.status === 200)) {
            if (!append) setReadings([]);
            setHasMore(false);
            return;
          }

          const logRows = response.data.result || [];
          setHasMore(logRows.length === PAGE_SIZE);
          setOffset(currentOffset + logRows.length);

          const details = await Promise.all(
            logRows.map((row) =>
              APIClient({
                method: 'GET',
                url: `${config.meter}readings/${row.readingId}`,
                headers: { Authorization: Autoload.Utils.getAuthToken() },
              })
                .then((detailResponse) =>
                  detailResponse.data.status && detailResponse.status === 200 ? detailResponse.data.result : null
                )
                .catch(() => null)
            )
          );

          const mapped = logRows
            .map((row, index) => (details[index] ? SmokeDetectorDataHandler.buildReadingSummary(row, details[index]) : null))
            .filter(Boolean);
          setReadings((prev) => (append ? [...prev, ...mapped] : mapped));
        })
        .catch(() => {
          if (!append) setReadings([]);
          setHasMore(false);
        })
        .finally(() => (append ? setLoadingMore : setLoading)(false));
    },
    [propertySelected]
  );

  useEffect(() => {
    setReadings([]);
    setOffset(0);
    setHasMore(true);
    fetchBatch(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertySelected?.id]);

  // Property-wide "what smoke detectors exist at all" list for the coverage check
  // (DetectorsFoundBadge) — independent of any single reading, so fetched once per property
  // rather than per batch.
  // Matches the same meter-list endpoint/pagination convention already used by MasterData.js
  // (results=100, no further pages requested — a property with >100 meters would be truncated,
  // same known limitation as every other call site of this endpoint).
  useEffect(() => {
    if (!propertySelected?.id) {
      setInstalledDetectorMeterNos([]);
      return;
    }
    APIClient({
      method: 'GET',
      url: `${config.meter}meters?propertyId=${propertySelected.id}&offset=0&results=100`,
      headers: { Authorization: Autoload.Utils.getAuthToken() },
    })
      .then((response) => {
        if (!(response.data.status && response.status === 200)) {
          setInstalledDetectorMeterNos([]);
          return;
        }
        const meters = response.data.result || [];
        setInstalledDetectorMeterNos(
          meters.filter(SmokeDetectorDataHandler.isSmokeDetectorMeter).map((meter) => meter.meterNo).filter(Boolean)
        );
      })
      .catch(() => setInstalledDetectorMeterNos([]));
  }, [propertySelected?.id]);

  return (
    <>
      <div style={{ display: 'flex', marginBottom: '14px' }}>
        {breadcrumbsContent.map((item) => item.area === '/meter/smoke_detectors' &&
          item.breadcrumbs.map((breadcrumb) =>
            breadcrumb.view === (isMobile ? 'mobile' : 'desktop') && (
              <div key={breadcrumb.label} style={{ display: 'flex', width: 'auto', alignItems: 'center', cursor: 'pointer', marginRight: '22px' }} onClick={() => window.Intercom('showArticle', breadcrumb.id)}>
                <img src={HelpLogo} alt="" />
                <span style={{ color: '#5555FF', marginLeft: '5px', fontSize: '14px' }}>{breadcrumb.label}</span>
              </div>
            )
          ))}
      </div>
      <div className="divider" />
      {loading ? (
        <Stack gap={0}>
          {Array.from({ length: 4 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ReadingRowSkeleton key={index} />
          ))}
        </Stack>
      ) : readings.length !== 0 ? (
        <Stack gap={0}>
          {readings.map((reading) => (
            <ReadingAccordion
              key={reading.id}
              reading={reading}
              propertySelected={propertySelected}
              profileData={profileData}
              accountData={accountData}
              installedDetectorMeterNos={installedDetectorMeterNos}
            />
          ))}
          {hasMore && (
            <Group justify="center" mt="md">
              <Button style={{ fontSize: '12px' }} variant="light" color='#5555FF' onClick={() => fetchBatch(offset, true)} loading={loadingMore}>
                Mehr laden
              </Button>
            </Group>
          )}
        </Stack>
      ) : (
        <Group justify="center" style={{ background: 'rgb(247, 247, 250)' }}>
          <EmptyStateContainer
            heading="Es wurden noch keine Rauchwarnmelder-Ablesungen gefunden."
            subheading="Laden Sie diese Seite neu, um zu sehen, ob in der Zwischenzeit Daten bereitgestellt wurden."
            icon={MeterIcon}
            showBtn={false}
            styleMainContainer={{ margin: 50 }}
          />
        </Group>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  propertySelected: state.generalReducer.property,
  profileData: state.generalReducer.profile,
  accountData: state.generalReducer.accountData,
});

export default connect(mapStateToProps)(SmokeDetectorReadingsLog);
